import { captureEvent } from '@lib/observability/events/captureEvent';

import { ANALYTICS_EVENTS } from '../../types/everything-bar-constants';
import type { DealWizardEventContext } from '../types/deal-builder-update-target';

/** Which engine a deal-wizard session ran on. */
function engineFor(wizardId: string | undefined): 'hardcoded' | 'wizard' {
  return wizardId ? 'wizard' : 'hardcoded';
}

function contextProps(ctx: DealWizardEventContext | undefined) {
  return {
    target: ctx?.target ?? 'deal',
    ...(ctx?.entry ? { entry: ctx.entry } : {}),
    ...(ctx?.dealFieldsWritten !== undefined
      ? { deal_fields_written: ctx.dealFieldsWritten }
      : {}),
  };
}

/**
 * One event on both sides of the flip.
 *
 * The built-in Deal Wizard row is suppressed when a data-driven wizard is the
 * default, and its `deal_wizard_clicked` would vanish with it — taking
 * wizard-launch visibility with it at exactly the moment the two engines need
 * comparing. Emitting from the launcher instead also closes the older blind spot
 * where custom-wizard launches reported nothing at all.
 */
export function trackDealWizardLaunched(
  wizardId: string | undefined,
  ctx?: DealWizardEventContext
): void {
  captureEvent(ANALYTICS_EVENTS.DEAL_WIZARD_LAUNCHED, {
    engine: engineFor(wizardId),
    wizard_id: wizardId,
    ...contextProps(ctx),
  });
}

/**
 * Completion, tagged identically. Launches alone cannot answer the pilot's only
 * question — do people finish as often on the data-driven engine? The drop-off
 * between the two is what decides whether the flag widens.
 */
export function trackDealWizardCompleted(
  wizardId: string | undefined,
  ctx?: DealWizardEventContext
): void {
  captureEvent(ANALYTICS_EVENTS.DEAL_WIZARD_COMPLETED, {
    engine: engineFor(wizardId),
    wizard_id: wizardId,
    ...contextProps(ctx),
  });
}
